import { BaseErrorInterface, LoggerInterface, errorDetails, details, BaseErrorParams, V8ErrorConstructor } from './types';
import _ from 'lodash';

const MESSAGE_CHARACTER_LIMIT = 200;
class BaseError extends Error implements BaseErrorInterface, LoggerInterface {
	errorId?: number;
	visualisers: object;
	tag?: string;
	errorDetails?: errorDetails;
	details?: details;
	messageType?: string;
	originalErrors: (Error | ErrorEvent | Event | BaseError | string)[];
	keepOriginalError: boolean;

	constructor(
		{
			message,
			errorId,
			name = 'BaseError',
			tag = '',
			errorDetails,
			details,
			visualisers,
			messageType,
			keepOriginalError = false
		}: BaseErrorParams) {

		super(message?.substring(0, MESSAGE_CHARACTER_LIMIT));

		// Adjust prototype chain:
		// github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
		Object.setPrototypeOf(this, BaseError.prototype);

		if ('captureStackTrace' in Error) {
			(Error as V8ErrorConstructor).captureStackTrace(this, this.constructor);
		}

		this.name = name;
		this.errorId = errorId;
		this.tag = tag;
		this.visualisers = visualisers;
		this.messageType = messageType;
		this.keepOriginalError = keepOriginalError;

		if (errorDetails) {
			this.errorDetails = errorDetails;
		}

		if (details) {
			this.details = details;
		}
	}

	public get errorid() { // backward compatibility as util was using lowercase errorid
		return this.errorId;
	}

	protected processOriginalError(originalError: Error | Event | ErrorEvent | BaseError | string): void {
		// Push originalError to array of all past errors
		const sourceObject = originalError instanceof ErrorEvent ? originalError.error : originalError;
		const previousErrors = _.get(sourceObject, 'originalErrors');
		this.originalErrors = [];

		if (!_.isEmpty(previousErrors)) {
			previousErrors.forEach(err => {
				this.originalErrors.push(err);
			});

			if (sourceObject instanceof BaseError && sourceObject?.originalErrors?.length) {
				if(!this.keepOriginalError) {
					sourceObject.originalErrors = [];
				}
			}
		}

		let itemToSave;
		if (typeof sourceObject === 'string') {
			itemToSave = sourceObject?.substring(0, MESSAGE_CHARACTER_LIMIT);
		} else if (_.get(sourceObject, 'message')) {
			if (sourceObject?.message.length > MESSAGE_CHARACTER_LIMIT) {
				const descriptor = Object.getOwnPropertyDescriptor(sourceObject, 'message');
				if (descriptor && descriptor.writable) {
					sourceObject.message = sourceObject?.message?.substring(0, MESSAGE_CHARACTER_LIMIT);
				}
			}
			itemToSave = sourceObject;
		} else {
			itemToSave = sourceObject;
		}

		this.originalErrors.push(itemToSave);
	}

	public getPropertiesToLogAsJson(): object | null {
		const results = {
			...this.errorDetails
		};
		return !_.isEmpty(results) ? results : null;
	}

	public getPropertiesToLog(): object | null {
		const results = {
			...this.details
		};
		return !_.isEmpty(results) ? results : null;
	}

	public getErrorHistoryToLog():string[] | undefined {
		let result;
		if (this.originalErrors?.length) {
			result = this.originalErrors.map((error, i) => {
				try {
					return JSON.stringify(error, this.extractProperties, 2);
				} catch (e) {
					return `JSON.stringify could not serialize object: ${e.message}`;
				}
			});
		}
		return result;
	}

	public getMesageFromHistoryAtIndex(index: number): Error | Event | ErrorEvent | BaseError | string | undefined {
		let result;
		if (this.originalErrors?.length) {
			const selectItemAtIndex = index >= 0 ? index : this.originalErrors.length + index;
			const selectedMessage = this.originalErrors[selectItemAtIndex];
			result = selectedMessage;
		}
		return result;
	}

	private extractProperties(key, errorItem) {
		if (errorItem instanceof Error || errorItem instanceof Event || errorItem instanceof ErrorEvent) {

			const startObject:any = {};

			if (errorItem instanceof Error) {
				startObject.stack = errorItem.stack;
				startObject.name = errorItem.name;
				startObject.message = errorItem.message;
			}

			const newValue = Object.getOwnPropertyNames(errorItem)
				.reduce((obj, propName) => {
					// Remove originalErrors property kept by using 'keepOriginalError' flag
					if (propName === 'originalErrors' || propName === 'keepOriginalError') {
						return obj;	
					}
					obj[propName] = errorItem[propName];
					return obj;
				}, startObject);
			return newValue;
		} else {
			return errorItem;
		}
	}
}

export default BaseError;


// WEBPACK FOOTER //
// ./src/errors/baseerror.ts